/**
 * Utilitários para formatação de datas e horários
 * 
 * O servidor EC2 está em UTC. O PostgreSQL tem timezone configurado mas
 * os timestamps são salvos como TIMESTAMP WITHOUT TIME ZONE em UTC.
 * O frontend deve interpretar como UTC e converter para o timezone local do browser.
 */

/**
 * Parseia o timestamp do backend para Date.
 * Se não tem timezone suffix, assume UTC (adiciona Z).
 * @param {string|Date} valor - Timestamp do backend
 * @returns {Date} Date object
 */
const parseTimestamp = (valor) => {
  if (!valor) return null;
  if (valor instanceof Date) return valor;
  const str = String(valor);
  // Se não tem timezone info (ex: "2026-07-30 14:45:13.221314"), assume UTC
  if (!str.match(/[Zz]$/) && !str.match(/[+-]\d{2}:\d{2}$/)) {
    return new Date(str.replace(' ', 'T') + 'Z');
  }
  return new Date(str);
};

/**
 * Formata horário (ex: "14:45")
 */
export const formatarHorario = (data) => {
  const d = parseTimestamp(data);
  if (!d || isNaN(d)) return '';
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
};

/**
 * Formata data e hora (ex: "30/07/2026, 14:45")
 */
export const formatarDataHora = (data) => {
  const d = parseTimestamp(data);
  if (!d || isNaN(d)) return '';
  return d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
};

/**
 * Formata data (ex: "30/07/2026")
 */
export const formatarData = (data) => {
  const d = parseTimestamp(data);
  if (!d || isNaN(d)) return '';
  return d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
};

/**
 * Formata separador de data no chat (ex: "Hoje", "Ontem", "30/07/2026")
 */
export const formatarDataSeparador = (data) => {
  const d = parseTimestamp(data);
  if (!d || isNaN(d)) return '';
  
  const agora = new Date();
  const hoje = new Date(agora.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
  hoje.setHours(0, 0, 0, 0);
  
  const dataMsg = new Date(d.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
  dataMsg.setHours(0, 0, 0, 0);
  
  const diffDias = Math.floor((hoje - dataMsg) / (1000 * 60 * 60 * 24));
  
  if (diffDias === 0) return 'Hoje';
  if (diffDias === 1) return 'Ontem';
  if (diffDias < 7) {
    const diaSemana = d.toLocaleDateString('pt-BR', { weekday: 'short', timeZone: 'America/Sao_Paulo' }).replace('.', '');
    return diaSemana;
  }
  return d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
};

/**
 * Formata horário para lista de contatos (ex: "14:45", "Ontem", "30/07/2026")
 */
export const formatarHorarioContato = (data) => {
  const d = parseTimestamp(data);
  if (!d || isNaN(d)) return '';
  
  const agora = new Date();
  const hoje = new Date(agora.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
  hoje.setHours(0, 0, 0, 0);
  
  const dataMsg = new Date(d.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
  dataMsg.setHours(0, 0, 0, 0);
  
  const diffDias = Math.floor((hoje - dataMsg) / (1000 * 60 * 60 * 24));
  
  if (diffDias === 0) return formatarHorario(data);
  if (diffDias === 1) return 'Ontem';
  if (diffDias < 7) {
    const diaSemana = d.toLocaleDateString('pt-BR', { weekday: 'short', timeZone: 'America/Sao_Paulo' }).replace('.', '');
    return diaSemana;
  }
  return d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
};

/**
 * Converte ISO UTC para datetime-local (para inputs de formulário)
 * Ex: "2026-07-30T14:45:00.000Z" → "2026-07-30T11:45" (em BRT)
 */
export const isoParaDatetimeLocal = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (isNaN(d)) return '';
  const brt = new Date(d.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
  const ano = brt.getFullYear();
  const mes = String(brt.getMonth() + 1).padStart(2, '0');
  const dia = String(brt.getDate()).padStart(2, '0');
  const hora = String(brt.getHours()).padStart(2, '0');
  const min = String(brt.getMinutes()).padStart(2, '0');
  return `${ano}-${mes}-${dia}T${hora}:${min}`;
};

/**
 * Converte datetime-local para ISO UTC (para enviar ao backend)
 * Ex: "2026-07-30T11:45" (BRT) → "2026-07-30T14:45:00.000Z"
 */
export const datetimeLocalParaISO = (datetimeLocal) => {
  if (!datetimeLocal) return '';
  // datetime-local é o horário que o usuário escolheu em BRT
  // Precisamos converter para UTC adicionando 3h
  const d = new Date(datetimeLocal);
  d.setHours(d.getHours() + 3);
  return d.toISOString();
};
